import type { NotaPublicaCard } from '@ahora-patagonia/contracts';
import { ApiError, api } from '../../shared/http/ApiClient.js';
import { BaseViewModel } from '../../shared/viewmodels/BaseViewModel.js';
import { CategoriaRepository } from '../models/repositories/CategoriaRepository.js';
import type { CategoriaData } from '../models/entities/CategoriaData.js';

interface State {
  loading: boolean;
  /** La última página traída (shell, categoría, total). */
  data: CategoriaData | null;
  /** Todas las notas acumuladas: "Ver más" appendea de a 12, no reemplaza. */
  items: NotaPublicaCard[];
  cargandoMas: boolean;
  error: string | null;
  noEncontrada: boolean;
  hydrated: boolean;
}

/** ViewModel de la página de sección, con "Ver más" de 12 en 12. */
export class CategoriaViewModel extends BaseViewModel<State> {
  private readonly repo = new CategoriaRepository({ get: (p) => api.get(p) });
  /** La última sección pedida. Lo que vuelva de una anterior se descarta. */
  private ultimaPedida: string;

  constructor(
    initial: CategoriaData | null,
    private urlKey: string,
  ) {
    super({
      loading: false,
      data: initial,
      items: initial?.items ?? [],
      cargandoMas: false,
      error: null,
      noEncontrada: false,
      hydrated: initial !== null,
    });
    this.ultimaPedida = urlKey;
  }

  /** Primera carga: no-op si la sección vino del server. */
  async load(): Promise<void> {
    await this.irA(this.urlKey);
  }

  /**
   * Muestra otra sección **sin remontar la pantalla**: del menú de la cabecera se salta de
   * una sección a otra por la misma ruta, así que solo cambian los params.
   */
  async irA(urlKey: string): Promise<void> {
    const actual = this.state$.value;
    if (urlKey === this.urlKey && actual.hydrated) return;

    this.urlKey = urlKey;
    this.ultimaPedida = urlKey;
    // Se vacía la lista: son las notas de la sección anterior y "Ver más" pagina sobre
    // ellas. La vista muestra el esqueleto mientras tanto.
    this.patch({
      loading: true,
      data: null,
      items: [],
      error: null,
      noEncontrada: false,
      hydrated: false,
    });

    try {
      const data = await this.repo.porUrlKey(urlKey);
      if (this.ultimaPedida !== urlKey) return;
      this.patch({ data, items: data.items, loading: false, hydrated: true });
    } catch (err) {
      if (this.ultimaPedida !== urlKey) return;
      if (err instanceof ApiError && err.status === 404) {
        this.patch({ loading: false, noEncontrada: true });
        return;
      }
      this.patch({ loading: false, error: (err as Error).message });
      this.emit('error', err);
    }
  }

  get hayMas(): boolean {
    const { data, items } = this.state$.value;
    return data !== null && items.length < data.total;
  }

  async verMas(): Promise<void> {
    const { data, items, cargandoMas } = this.state$.value;
    if (!data || cargandoMas || !this.hayMas) return;

    this.patch({ cargandoMas: true });
    try {
      const siguiente = Math.floor(items.length / data.pageSize) + 1;
      const pagina = await this.repo.porUrlKey(this.urlKey, siguiente);
      const vistos = new Set(items.map((n) => n.id));
      this.patch({
        data: pagina,
        items: [...items, ...pagina.items.filter((n) => !vistos.has(n.id))],
        cargandoMas: false,
      });
    } catch (err) {
      this.patch({ cargandoMas: false, error: (err as Error).message });
      this.emit('error', err);
    }
  }
}
